import { Component, EventEmitter, Input, Output, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { WebApiClient } from 'src/app/services/web-api-client.service';
import { WebApiService } from 'src/app/services/web-api.service';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { PageData } from 'src/app/shared/models/page-data';
import { BatchAddProductDto, BatchAddProductQuery } from './models/batch-add-product';
import { VisGridComponent } from 'src/app/shared/components/grid/vis-grid/vis-grid.component';

@Component({
  selector: 'app-stocks-batch-add-product-dialog',
  templateUrl: './batch-add-product-dialog.component.html',
  styleUrls: ['./batch-add-product-dialog.component.css']
})
export class BatchAddProductDialogComponent implements OnInit {

  @ViewChild(VisGridComponent) grid: VisGridComponent;

  constructor(
    private fb: FormBuilder,
    private webApiClient: WebApiClient,
    private notification: EasyNotificationService
  ) {
    this.api = this.webApiClient.createHisService('stocks/ProductBatchAdd');
    this.queryFormGroup = this.createQueryFormGroup();
  }

  // API 服務
  private api: WebApiService;

  // @Input 屬性
  @Input() show: boolean = false;
  @Input() title: string = '批次新增商品';
  @Input() enableStockSystem: boolean = false;
  /** 庫別 ID，傳入後會用於 API 過濾已開帳商品 */
  @Input() storeRoomId: number = 0;
  /** 是否只搜尋計算庫存的商品 (true=只搜尋庫存商品, null/false=不篩選) */
  @Input() isInventory: boolean = null;
  /** 商品類型篩選 (1=一般商品, 2=組合商品, null=不篩選) */
  @Input() productType: number = null;
  /** 進貨稅別篩選 (0=免稅, 1=應稅, null=不篩選) */
  @Input() stockTaxType: number = null;
  /** 銷售稅別篩選 (0=免稅, 1=應稅, null=不篩選) */
  @Input() saleTaxType: number = null;
  /** 廠商代碼精確篩選 (用於進貨作業依單據廠商篩選) */
  @Input() filterVendorCode: string = null;
  /** 排除停止退貨的商品 (true=排除, null=不篩選) */
  @Input() excludeStopReturn: boolean = null;
  /** 排除停止進貨的商品 (true=排除, null=不篩選) */
  @Input() excludeStopStock: boolean = null;
  /** 排除停止銷售的商品 (true=排除, null=不篩選) */
  @Input() excludeStopSale: boolean = null;
  // @Output 事件
  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<BatchAddProductDto[]>();

  // 查詢表單
  queryFormGroup: FormGroup;

  // 商品清單
  listItems: BatchAddProductDto[] = [];
  pageData: PageData = { total: 0, totalPages: 0, data: [] };

  // 表格相關
  gridView: any = { data: [], total: 0 };
  pageSize: number = 10;
  pageSkipNo = 0;

  // 選擇相關（跨頁保留）
  selectedItems: number[] = [];
  // 上次查詢條件 (確保全選結果與查詢結果一致)
  private lastQueryConditions: any = null;

  // 跨頁商品資料快取 (用於記錄所有載入過的商品完整資料)
  private allProductsMap: Map<number, BatchAddProductDto> = new Map();

  // 編輯選項
  editOptions = {
    rxTypes: [],
    vendors: []
  };

  // 廠商下拉選單選項 (valuetext 格式: value=Code, text=Name)
  vendorOptions: { value: string; text: string }[] = [];

  ngOnInit() {
    this.queryFormGroup = this.createQueryFormGroup();
    this.getEditOptions();
  }

  // 取得編輯選項
  private getEditOptions() {
    this.api.get('GetEditOptions').subscribe(
      (data: any) => {
        this.editOptions = data;
        this.buildVendorOptions();
      },
      error => {
        this.notification.showGetDataFailed();
      }
    );
  }

  // 建立廠商下拉選單選項
  private buildVendorOptions() {
    this.vendorOptions = [
      { value: '', text: '' },
      ...(this.editOptions.vendors || []).map(item => ({
        value: item.Code,
        text: item.Name
      }))
    ];
  }

  // 建立查詢表單
  private createQueryFormGroup(): FormGroup {
    return this.fb.group({
      productCode: [''],
      productCodeStart: [''],
      productCodeEnd: [''],
      productName: [''],
      vendorCode: [''],
      rxType: ['']
    });
  }

  // 取得庫別 ID（供 API 過濾用）
  private getStoreRoomId(): number {
    return this.storeRoomId > 0 ? this.storeRoomId : 0;
  }

  // Dialog 開啟時
  onDialogOpen() {
    this.resetState();
    this.refreshList(true);
  }

  // 重置狀態
  private resetState() {
    this.queryFormGroup.reset();
    this.listItems = [];
    this.pageSkipNo = 0;
    this.selectedItems = [];
    this.lastQueryConditions = null;
    this.allProductsMap.clear();  // 清空跨頁商品資料快取
    // 清空 grid 內部的跨頁選擇記憶
    if (this.grid) {
      this.grid.clearAllCrossPageSelection();
    }
  }

  // 關閉 Dialog
  closeDialog() {
    this.resetState();
    this.close.emit();
  }

  // 產生查詢條件 (不含分頁)
  private makeQueryConditions(): any {
    const rxTypeValue = this.queryFormGroup.get('rxType').value;
    const storeRoomId = this.getStoreRoomId();
    const conditions: any = {
      productCode: (this.queryFormGroup.get('productCode').value || '').trim(),
      productCodeStart: (this.queryFormGroup.get('productCodeStart').value || '').trim(),
      productCodeEnd: (this.queryFormGroup.get('productCodeEnd').value || '').trim(),
      productName: this.queryFormGroup.get('productName').value || '',
      vendorCode: this.queryFormGroup.get('vendorCode').value || '',
      storeRoomId: storeRoomId > 0 ? storeRoomId : null,
      isInventory: this.isInventory === true ? true : null,
      productType: this.productType != null ? this.productType : null,
      stockTaxType: this.stockTaxType != null ? this.stockTaxType : null,
      saleTaxType: this.saleTaxType != null ? this.saleTaxType : null,
      filterVendorCode: this.filterVendorCode || '',
      excludeStopReturn: this.excludeStopReturn === true ? true : null,
      excludeStopStock: this.excludeStopStock === true ? true : null,
      excludeStopSale: this.excludeStopSale === true ? true : null,
    };
    if (rxTypeValue) {
      conditions.rxType = rxTypeValue;
    }
    return conditions;
  }

  // 查詢按鈕
  onBtnQueryClick() {
    this.pageSkipNo = 0;
    this.refreshList(true);
  }

  // 重新整理清單
  refreshList(isNewQuery?: boolean) {
    if (isNewQuery) {
      this.lastQueryConditions = this.makeQueryConditions();
    }
    const query: BatchAddProductQuery = {
      ...(this.lastQueryConditions || this.makeQueryConditions()),
      pageSkip: this.pageSkipNo,
      pageSize: this.pageSize
    };

    this.api.get('GetAll', query).subscribe(
      (result: any) => {
        const data: BatchAddProductDto[] = result.data || [];
        // 設定商品清單，數量預設為 0，單位成本帶入進價
        const storeRoomId = this.getStoreRoomId();
        this.listItems = data.map(item => {
          const productItem: BatchAddProductDto = {
            ...item,
            storeRoomId: storeRoomId,
            qty: 0,
            unitCost: item.OrderPrice || 0
          };
          // 將商品資料存入快取 (跨頁保留)
          this.allProductsMap.set(item.Id, productItem);
          return productItem;
        });
        this.pageData = result;
        this.setGridView();
      },
      error => this.notification.showGetDataFailed()
    );
  }

  // 設定表格資料
  private setGridView() {
    this.gridView = {
      data: this.listItems,
      total: this.pageData?.total || 0
    };
  }

  // 分頁切換
  pageChange(pageNum: number): void {
    this.pageSkipNo = (pageNum - 1) * this.pageSize;
    this.refreshList();
  }

  // ==================== Checkbox 相關 ====================

  // 全選（選取所有符合篩選條件的商品）
  selectAll() {
    if (!this.lastQueryConditions) return;
    const query: BatchAddProductQuery = {
      ...this.lastQueryConditions,
      pageSkip: 0,
      pageSize: 99999  // 取得所有符合條件的商品
    };

    this.api.get('GetAll', query).subscribe(
      (result: any) => {
        const data: BatchAddProductDto[] = result.data || [];
        const storeRoomId = this.getStoreRoomId();
        const allIds: number[] = [];

        // 將全選的商品資料存入快取
        data.forEach(item => {
          allIds.push(item.Id);
          // 如果快取中沒有此商品，則加入快取
          if (!this.allProductsMap.has(item.Id)) {
            this.allProductsMap.set(item.Id, {
              ...item,
              storeRoomId: storeRoomId,
              qty: 0,
              unitCost: item.OrderPrice || 0
            });
          }
        });

        // 覆蓋選取項目（確保與當前查詢結果一致）
        this.selectedItems = [...allIds];
      },
      error => this.notification.showGetDataFailed()
    );
  }

  // 全取消
  deselectAll() {
    this.selectedItems = [];
    // 清空 grid 內部的跨頁選擇記憶
    if (this.grid) {
      this.grid.clearAllCrossPageSelection();
    }
  }

  // checkbox 選取變更（參考安全存量維護）
  checkChanged(evt: { checked: boolean, data: BatchAddProductDto }) {
    if (evt.data) {
      // 單一項目選取/取消選取
      const id = evt.data.Id;
      const hasId = this.selectedItems.some(x => x === id);
      if (evt.checked) {
        if (!hasId) {
          this.selectedItems = [...this.selectedItems, id];
        }
      } else {
        this.selectedItems = this.selectedItems.filter(x => x !== id);
      }
    } else {
      // 全選/取消全選（當前頁）
      const allIds = this.listItems.map(item => item.Id);
      if (evt.checked) {
        // 全選：將當前頁面所有項目加入 selectedItems (避免重複)
        const newItems = allIds.filter(id => !this.selectedItems.includes(id));
        this.selectedItems = [...this.selectedItems, ...newItems];
      } else {
        // 取消全選：將當前頁面所有項目從 selectedItems 移除
        this.selectedItems = this.selectedItems.filter(id => !allIds.includes(id));
      }
    }
  }

  // ==================== 當前頁全選狀態 ====================

  // 當前頁是否全選
  get isCurrentPageAllChecked(): boolean {
    if (!this.listItems || this.listItems.length === 0) {
      return false;
    }
    const currentPageIds = this.listItems.map(item => item.Id);
    return currentPageIds.every(id => this.selectedItems.includes(id));
  }

  // 當前頁是否有部分選取
  get isCurrentPageSomeChecked(): boolean {
    if (!this.listItems || this.listItems.length === 0) {
      return false;
    }
    const currentPageIds = this.listItems.map(item => item.Id);
    const selectedCount = currentPageIds.filter(id => this.selectedItems.includes(id)).length;
    return selectedCount > 0 && selectedCount < currentPageIds.length;
  }

  // ==================== 確認新增 ====================

  // 取得已勾選的項目數量（包含跨頁）
  getSelectedCount(): number {
    return this.selectedItems.length;
  }

  // 確認新增
  confirmAdd() {
    if (this.selectedItems.length === 0) {
      this.notification.showWarning('請先勾選商品');
      return;
    }

    // 收集所有已勾選的項目資料 (從跨頁快取 Map 取得完整資料)
    const storeRoomId = this.getStoreRoomId();
    const selectedProductItems: BatchAddProductDto[] = [];

    // 從 selectedItems（ID 陣列）收集完整資料
    this.selectedItems.forEach(id => {
      // 優先從跨頁快取 Map 取得完整資料
      const cachedItem = this.allProductsMap.get(id);
      if (cachedItem) {
        selectedProductItems.push(cachedItem);
      } else {
        // 快取中找不到（理論上不應發生），使用基本資料
        selectedProductItems.push({
          Id: id,
          ProductCode: '',
          ProductName: '',
          VendorCode: '',
          VendorName: '',
          Unit: '',
          storeRoomId: storeRoomId,
          qty: 0,
          unitCost: 0
        });
      }
    });

    // 回傳已勾選的項目（數量和金額預設為 0）
    // 注意：不要在這裡呼叫 closeDialog()，讓父元件在 API 完成後控制關閉
    this.confirm.emit(selectedProductItems);
  }
}
